// src/components/WeatherAlertCard.jsx
// One weather alert + the Still On / Cancel / Postpone response buttons.
// Shared by:
//   • WeatherAlertBanner (dashboard in-app banner)
//   • WeatherAlertResponse page (focused landing for email/push CTA)
//
// Props:
//   alert:       { id, severity, eventLabel, weatherLabel, precipPct, windMph,
//                  location, teamId, status, ... }
//   teamId:      for the "Draft Message to Families" navigation target
//   onResponded: optional callback(status) fired after a successful response.
//                Lets the banner keep its dismiss-on-respond behavior while
//                the focused page can use it to auto-advance / show follow-up.
//   dense:       compact padding (banner) vs roomy (focused page)

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';
import clsx from 'clsx';

import {
  BoltIcon,
  CloudIcon,
  CheckCircleIcon,
  XCircleIcon,
  CalendarDaysIcon,
  PaperAirplaneIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const SEVERITY_STYLES = {
  critical: {
    wrapper: 'border-red-300 dark:border-red-800/60 bg-red-50 dark:bg-red-900/15',
    icon:    'text-red-600 dark:text-red-400',
    iconBg:  'bg-red-100 dark:bg-red-900/40',
    badge:   'bg-red-600 text-white',
    Icon:    BoltIcon,
  },
  high: {
    wrapper: 'border-orange-300 dark:border-orange-800/60 bg-orange-50 dark:bg-orange-900/15',
    icon:    'text-orange-600 dark:text-orange-400',
    iconBg:  'bg-orange-100 dark:bg-orange-900/40',
    badge:   'bg-orange-500 text-white',
    Icon:    CloudIcon,
  },
  moderate: {
    wrapper: 'border-amber-300 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/15',
    icon:    'text-amber-600 dark:text-amber-400',
    iconBg:  'bg-amber-100 dark:bg-amber-900/40',
    badge:   'bg-amber-500 text-white',
    Icon:    CloudIcon,
  },
};

function conditionLine(alert) {
  const parts = [alert.weatherLabel];
  if (alert.precipPct > 0)  parts.push(`${alert.precipPct}% precip`);
  if (alert.windMph   > 0)  parts.push(`${alert.windMph}mph winds`);
  if (alert.location?.name) parts.push(`at ${alert.location.name}`);
  return parts.join(' · ');
}

export default function WeatherAlertCard({
  alert,
  teamId,
  onResponded,
  dense       = false,
  dismissible = true,
}) {
  const navigate = useNavigate();
  const [loading,  setLoading]  = useState(null);     // 'confirmed'|'cancelled'|'postponed'
  const [responded, setResponded] = useState(alert.status !== 'pending');
  const [responseStatus, setResponseStatus] = useState(
    alert.status !== 'pending' ? alert.status : null,
  );
  const [dismissed, setDismissed] = useState(false);

  const styles = SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.moderate;
  const { Icon } = styles;

  const respond = async (status) => {
    setLoading(status);
    try {
      const fn = httpsCallable(getFunctions(), 'respondToWeatherAlert');
      await fn({ alertId: alert.id, status });
      setResponseStatus(status);
      setResponded(true);
      toast.success(
        status === 'confirmed' ? 'Marked as still on ✅' :
        status === 'cancelled' ? 'Event cancelled.' :
        'Event postponed.',
      );
      onResponded?.(status);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  if (dismissed) return null;

  return (
    <div className={clsx(
      'relative rounded-2xl border transition-all',
      dense ? 'p-4 sm:p-5' : 'p-5 sm:p-7',
      styles.wrapper,
    )}>
      {/* Dismiss (only after responding, and only when dismissible) */}
      {responded && dismissible && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="absolute top-3 right-3 p-1 rounded-lg text-zinc-400 hover:text-zinc-600 hover:bg-white/50 transition-colors"
          aria-label="Dismiss"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}

      {/* Header row */}
      <div className="flex items-start gap-3">
        <div className={clsx(
          'flex-shrink-0 rounded-xl',
          dense ? 'p-2.5' : 'p-3',
          styles.iconBg,
        )}>
          <Icon className={clsx(dense ? 'h-5 w-5' : 'h-6 w-6', styles.icon)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className={clsx(
              'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide',
              styles.badge,
            )}>
              {alert.severity === 'critical' ? '⚡ Lightning Risk' :
               alert.severity === 'high'     ? '🌧️ Severe Weather' :
               '☁️ Weather Alert'}
            </span>
          </div>
          <p className={clsx(
            'font-bold text-zinc-900 dark:text-white leading-snug',
            dense ? 'text-sm' : 'text-base sm:text-lg',
          )}>
            {alert.eventLabel}
          </p>
          <p className={clsx(
            'mt-0.5 text-zinc-600 dark:text-zinc-400',
            dense ? 'text-xs' : 'text-sm',
          )}>
            {conditionLine(alert)}
          </p>
        </div>
      </div>

      {/* Response area */}
      <div className={dense ? 'mt-4' : 'mt-5'}>
        {!responded ? (
          <>
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
              Is this event still happening?
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={!!loading}
                onClick={() => respond('confirmed')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-300 dark:border-emerald-700 bg-white dark:bg-zinc-800 px-3.5 py-2 text-xs font-bold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors disabled:opacity-60"
              >
                {loading === 'confirmed'
                  ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  : <CheckCircleIcon className="h-3.5 w-3.5" />
                }
                Still On
              </button>

              <button
                type="button"
                disabled={!!loading}
                onClick={() => respond('cancelled')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-red-300 dark:border-red-700 bg-white dark:bg-zinc-800 px-3.5 py-2 text-xs font-bold text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60"
              >
                {loading === 'cancelled'
                  ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  : <XCircleIcon className="h-3.5 w-3.5" />
                }
                Cancel
              </button>

              <button
                type="button"
                disabled={!!loading}
                onClick={() => respond('postponed')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 dark:border-amber-700 bg-white dark:bg-zinc-800 px-3.5 py-2 text-xs font-bold text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors disabled:opacity-60"
              >
                {loading === 'postponed'
                  ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                  : <CalendarDaysIcon className="h-3.5 w-3.5" />
                }
                Postpone
              </button>
            </div>
          </>
        ) : responseStatus === 'confirmed' ? (
          <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
            <CheckCircleIcon className="h-4 w-4" />
            Marked as still on. Families won't receive a cancellation.
          </div>
        ) : (
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 p-4">
            <p className="text-xs font-bold text-zinc-900 dark:text-white mb-0.5">
              {responseStatus === 'cancelled' ? '✅ Event cancelled.' : '✅ Event postponed.'}
            </p>
            <p className="text-[11px] text-zinc-500 dark:text-zinc-400 mb-3">
              Families on SMS or email have been notified. Want to add a personal message?
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/teams/${teamId}/messages/new?type=${encodeURIComponent('Schedule Change')}&context=weather_${responseStatus}`,
                  )
                }
                className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-3.5 py-2 text-xs font-bold text-white hover:bg-blue-500 transition-colors"
              >
                <PaperAirplaneIcon className="h-3.5 w-3.5" />
                Draft Message to Families
              </button>
              {dismissible && (
                <button
                  type="button"
                  onClick={() => setDismissed(true)}
                  className="rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3.5 py-2 text-xs font-semibold text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-700 transition-colors"
                >
                  Skip
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
